import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import VuiContainer from "../../../../../../@vodea/vodea-ui/components/Container";
import Button from "antd/es/button";
import { PlusOutlined } from "@ant-design/icons";
import VuiSectionTitle from "../../../../../../@vodea/vodea-ui/components/Sections/Title";
import Col from "antd/es/grid/col";
import Row from "antd/es/grid/row";
import Table from "antd/es/table";
import { AxiosError, AxiosResponse } from "axios";
import { IBreadcrumbs } from "../../../../../../stores/breadcrumbs";
import {
  VuiActionTable,
  VuiSearch,
} from "../../../../../../@vodea/vodea-ui/components";
import {
  ConstantService,
  ICollection,
  ITableQuery,
  UrlParamsType,
  useBreadcrumbs,
  useDebounce,
  useIsMounted,
} from "../../../../../../utils";
import { ColumnType } from "antd/es/table/interface";
import VuiContent from "../../../../../../@vodea/vodea-ui/components/Content";
import CompanyRepository from "../../../../../../repositories/CompanyRepository";
import { Avatar } from "antd";
import { getAliasesName } from "../../../../../../functions/global";
import VuiAccessible from "../../../../../../@vodea/vodea-ui/components/Accessible";
import { ACCESS_PERMISSION } from "../../../../../../constant";

const qs = require("qs");

interface IParams extends UrlParamsType {
  with: string[];
}

interface IAppSettingAccountCompanyList {
  breadcrumbs: IBreadcrumbs[];
}

const AppSettingAccountCompanyList: FC<IAppSettingAccountCompanyList> = ({
  breadcrumbs,
}) => {
  useBreadcrumbs(breadcrumbs);
  const hasInitialize = useRef(false);
  const { t } = useTranslation();
  const isMounted = useIsMounted();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState<string>(
    searchParams.get("search") || ""
  );
  const debouncedSearch = useDebounce<string>(search, 250);
  const [tableQuery, setTableQuery] = useState<ITableQuery>({
    page: Number(
      searchParams.get("page") || ConstantService.getConstant("DEFAULT_PAGE")
    ),
    perPage: Number(
      searchParams.get("perPage") ||
        ConstantService.getConstant("DEFAULT_PER_PAGE")
    ),
    sortField: searchParams.get("sortField") || null,
    sortOrder: searchParams.get("sortOrder") || null,
  });
  const [tableLoading, setTableLoading] = useState<boolean>(false);
  const [tableData, setTableData] = useState<any[]>([]);
  const [totalData, setTotalData] = useState<number>(0);
  const tableColumns: ColumnType<any>[] = [
    {
      title: t("common.text.name"),
      dataIndex: "name",
      key: "name",
      render: (text: any, record: any) => (
        <>
          <Avatar className="mr16" src={record?.logo?.url}>
            {getAliasesName(text)}
          </Avatar>
          {text}
        </>
      ),
    },
    {
      title: "",
      key: "action",
      render: (text: any, record: any) => (
        <VuiAccessible access={ACCESS_PERMISSION.company.show}>
          <VuiActionTable<any> detailLink={`${pathname}/${record.id}`} />
        </VuiAccessible>
      ),
    },
  ];

  const loadData = useCallback(
    async (searchParams) => {
      if (!isMounted) {
        return;
      }

      setTableLoading(true);

      const params: IParams = {
        search: searchParams.get("search"),
        page: Number(searchParams.get("page")),
        per_page: Number(searchParams.get("perPage")),
        order_by: searchParams.get("sortField"),
        sorted_by: searchParams.get("sortOrder"),
        with: ["logo"],
      };

      await CompanyRepository.findAll(params)
        .then((response: AxiosResponse<ICollection<any[]>>) => {
          if (isMounted) {
            const { data: responseData, meta } = response.data;
            setTableLoading(false);
            setTableData(responseData);
            setTotalData(meta.total);
          }
        })
        .catch((error: AxiosError) => {
          if (isMounted) {
            setTableLoading(false);
          }
        });

      hasInitialize.current = true;
    },
    [isMounted]
  );

  const handleTableChange = useCallback(
    (pagination: any, filters: any, sorter: any, extra: any) => {
      setTableQuery((prevState) => ({
        ...prevState,
        page: pagination.current,
        perPage: pagination.pageSize,
        ...(sorter.order ? { sortOrder: sorter.order } : {}),
        ...(sorter.field ? { sortField: sorter.field } : {}),
      }));
    },
    []
  );

  const setQueryParams = useCallback(() => {
    const params: Partial<IParams> = {};

    Object.assign(params, {
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
      ...(tableQuery.page ? { page: tableQuery.page } : {}),
      ...(tableQuery.perPage ? { perPage: tableQuery.perPage } : {}),
      ...(tableQuery.sortField ? { sortField: tableQuery.sortField } : {}),
      ...(tableQuery.sortOrder ? { sortOrder: tableQuery.sortOrder } : {}),
    });

    const queryParams = qs.stringify(params, { indices: false });

    if (queryParams) {
      setSearchParams(`?${queryParams}`);
    } else {
      navigate("");
    }
  }, [setSearchParams, navigate, debouncedSearch, tableQuery]);

  useMemo(() => {
    (async () => {
      await loadData(searchParams);
    })();
  }, [searchParams]);

  useEffect(() => {
    isMounted ? setQueryParams() : setSearchParams("");
  }, [debouncedSearch, isMounted, tableQuery]);

  useEffect(() => {
    if (isMounted && hasInitialize.current) {
      setTableQuery((s) => ({
        ...s,
        page: Number(ConstantService.getConstant("DEFAULT_PAGE")),
      }));
    }
  }, [debouncedSearch]);

  return (
    <>
      <VuiContainer>
        <VuiSectionTitle title={t("common.text.company")}>
          <VuiAccessible access={ACCESS_PERMISSION.company.store}>
            <Button
              onClick={() => navigate(`${pathname}/add`)}
              type="primary"
              icon={<PlusOutlined />}
            >
              {t("common.button.add")}
            </Button>
          </VuiAccessible>
        </VuiSectionTitle>

        <VuiContent>
          <Row gutter={[10, 10]} className="mb24">
            <Col className="gutter-row" span={6} xs={24} md={12} lg={10}>
              <VuiSearch
                allowClear
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </Col>
          </Row>

          <Table
            rowKey={"id"}
            bordered
            columns={tableColumns}
            dataSource={tableData}
            loading={tableLoading}
            onChange={handleTableChange}
            pagination={
              tableData.length > 0
                ? {
                    current: tableQuery.page,
                    pageSize: tableQuery.perPage,
                    total: totalData,
                  }
                : false
            }
          />
        </VuiContent>
      </VuiContainer>
    </>
  );
};

export default AppSettingAccountCompanyList;
